/**
 * Zod 4 contracts for the Buxgalteriya (Accounting) module.
 *
 * No backend accounting aggregate exists yet. The frontend OWNS the
 * `/api/v1/accounting/*` contract (8 endpoints), serves it via MSW v2 now,
 * and swaps `baseURL` when the backend ships. CTO (Saydullo) has accepted
 * this contract, so THESE SCHEMAS ARE THE FORMAL BACKEND SPEC — keep them
 * precise. See `buxgalteriya-implementation-plan.md` §2.
 *
 * Every network response parses through `typedRequest` (the ACL boundary in
 * `@/shared/api/typed-request`); drift throws `SchemaParseError`. Form submit
 * re-parses the assembled payload with the matching Create schema.
 *
 * Enums are English SCREAMING_SNAKE; UI labels resolve through i18n + the
 * status/category meta maps (`model/status-meta.ts`, `api/taxonomy.ts`) — never
 * inline literals.
 */

import { z } from 'zod'
import { CATEGORY_IDS } from './taxonomy'

// ─── Enums ──────────────────────────────────────────────────────────────────
// UI labels (uzCYR/uzLAT/ruRUS) live in i18n; these are the wire values.

export const TxnType = z.enum(['INCOME', 'EXPENSE']) // prototype: kirim | chiqim
export type TxnType = z.infer<typeof TxnType>

export const TxnStatus = z.enum(['UNPAID', 'INVOICED', 'SETTLED']) // yangi | to'lovda | tasdiqlandi
export type TxnStatus = z.infer<typeof TxnStatus>

export const InvoiceType = z.enum(['EXPENSE', 'INCOME']) // PAY-* | RCT-*
export type InvoiceType = z.infer<typeof InvoiceType>

export const InvoiceStatus = z.enum(['PENDING', 'PAID'])
export type InvoiceStatus = z.infer<typeof InvoiceStatus>

export const PaymentMethod = z.enum(['CASH', 'TRANSFER', 'CARD', 'FX']) // naqd | perevod | plastik | valyuta
export type PaymentMethod = z.infer<typeof PaymentMethod>

export const Currency = z.enum(['UZS', 'USD'])
export type Currency = z.infer<typeof Currency>

export const Department = z.enum(['SUPPLY', 'TRANSPORT', 'SALES', 'HR', 'FACILITY', 'FINANCE'])
export type Department = z.infer<typeof Department>

export const AccountKind = z.enum(['CASH', 'CARD', 'BANK', 'FX'])
export type AccountKind = z.infer<typeof AccountKind>

// categoryId is validated against the fixed frontend taxonomy (api/taxonomy.ts).
// Backend may later expose GET /accounting/categories — this enum is the seam.
export const CategoryId = z.enum(CATEGORY_IDS)
export type CategoryId = z.infer<typeof CategoryId>

// Integer so'm. Comment-only ceiling: realistic max ~18.6e9 < Number.MAX_SAFE_INTEGER,
// so a plain int is safe — no BigInt, no decimal. Never render raw (use lib/money.ts).
const Money = z.number().int().nonnegative()
// Signed money (net result, account balance can go negative).
const SignedMoney = z.number().int()

// ─── Transaction ──────────────────────────────────────────────────────────────

export const TransactionSchema = z.object({
  id: z.string(), // "T-2605-001"
  date: z.string(), // ISO "2026-05-16"
  type: TxnType,
  categoryId: CategoryId,
  paymentMethod: PaymentMethod,
  amount: Money,
  currency: Currency,
  partyId: z.string().nullable(),
  partyName: z.string(),
  sourceRef: z.string().nullable(), // "PRM-2605-0007" — link to source doc (display-only)
  department: Department,
  note: z.string(),
  status: TxnStatus,
  invoiceId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(), // ISO datetime
})
export type Transaction = z.infer<typeof TransactionSchema>

// Create body — server assigns id/status/invoiceId/createdBy/createdAt.
export const CreateTransactionSchema = TransactionSchema.omit({
  id: true,
  status: true,
  invoiceId: true,
  createdBy: true,
  createdAt: true,
})
export type CreateTransaction = z.infer<typeof CreateTransactionSchema>

// PUT /transactions/{id} = full replace; same editable surface as create.
export const UpdateTransactionSchema = CreateTransactionSchema
export type UpdateTransaction = z.infer<typeof UpdateTransactionSchema>

// ─── Invoice ────────────────────────────────────────────────────────────────

export const InvoiceSchema = z.object({
  id: z.string(), // "PAY-2605-001" | "RCT-2605-001"
  type: InvoiceType,
  paymentMethod: PaymentMethod,
  accountId: z.string(),
  accountName: z.string(), // "Касса 1"
  date: z.string(), // ISO issue date
  paidAt: z.string().nullable(), // ISO
  payerName: z.string(),
  payeeName: z.string(),
  txnIds: z.array(z.string()).min(1), // one invoice covers 1+ txns
  status: InvoiceStatus,
  amount: Money, // = sum(covered txn.amount), server-computed
  currency: Currency,
  docNo: z.string(),
  note: z.string(),
  createdBy: z.string(),
})
export type Invoice = z.infer<typeof InvoiceSchema>

// Fields the client supplies when creating an invoice. Server assigns
// id/status/createdBy, computes amount from txnIds, and sets paidAt on markPaid.
export const CreateInvoiceFields = InvoiceSchema.omit({
  id: true,
  paidAt: true,
  txnIds: true,
  status: true,
  amount: true,
  createdBy: true,
})
export type CreateInvoiceFields = z.infer<typeof CreateInvoiceFields>

// POST /invoices — standalone invoice over an explicit set of txns.
export const CreateInvoiceSchema = CreateInvoiceFields.extend({
  txnIds: z.array(z.string()).min(1),
  // TODO[backend-swap]: confirm wire shape — the frontend currently
  // consolidates "create-and-pay" into a single mutation (paid:true triggers
  // the same cascade as PATCH /invoices/{id}/paid, taking covered txns
  // UNPAID → SETTLED). If the backend prefers POST /invoices then a
  // separate PATCH …/paid, drop this field and have the caller chain the
  // two mutations on the client side instead.
  paid: z.boolean().default(false),
})
export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>

// PUT /invoices/{id} = full replace while PENDING (locked once PAID — enforced
// server-side / by the txn-status guards, not by this schema).
export const UpdateInvoiceSchema = CreateInvoiceSchema
export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>

// ─── Attach-invoice payload (POST /transactions/{id}/attach-invoice) ──────────
// CREATE: open a new PENDING invoice over [id, ...extraTxnIds].
// LINK:   append id to an existing PENDING invoice; server recomputes amount.

export const AttachInvoicePayload = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('CREATE'),
    createInvoice: CreateInvoiceFields,
    extraTxnIds: z.array(z.string()).optional(),
  }),
  z.object({
    mode: z.literal('LINK'),
    invoiceId: z.string(),
  }),
])
export type AttachInvoicePayload = z.infer<typeof AttachInvoicePayload>

// ─── Dashboard summary (GET /dashboard/summary?from&to) ───────────────────────

export const DashboardKpi = z.object({
  incomeTotal: Money,
  expenseTotal: Money,
  netResult: SignedMoney, // income − expense; may be negative
  marginPct: z.number(), // netResult / incomeTotal * 100; server-computed so 0/0 cases stay consistent
  incomeDeltaPct: z.number(), // vs. previous period (Май → Апр), float (may be negative)
  expenseDeltaPct: z.number(),
  unpaidCount: z.number().int().nonnegative(),
  unpaidAmount: Money,
})
export type DashboardKpi = z.infer<typeof DashboardKpi>

export const DailyFlowPoint = z.object({
  date: z.string(), // ISO day
  income: Money,
  expense: Money,
})
export type DailyFlowPoint = z.infer<typeof DailyFlowPoint>

export const CategorySlice = z.object({
  categoryId: CategoryId,
  label: z.string(), // backend-supplied display label; UI may re-resolve via i18n
  amount: Money,
  color: z.string(),
})
export type CategorySlice = z.infer<typeof CategorySlice>

export const AccountBalance = z.object({
  id: z.string(),
  name: z.string(), // sub-label on the card — "Касса 1", "Капиталбанк ...4127", "UzCard ...8809", "Сейф · USD"
  kind: AccountKind, // CASH | CARD | BANK | FX — icon source
  method: PaymentMethod, // CASH | CARD | TRANSFER | FX — drives the big i18n-resolved label (payment_method.*)
  balance: SignedMoney,
  incomeDelta: Money, // arrow-up amount on the card (period income through this account)
  expenseDelta: Money, // arrow-down amount on the card (period expense through this account)
  currency: Currency,
})
export type AccountBalance = z.infer<typeof AccountBalance>

export const TopParty = z.object({
  partyName: z.string(),
  amount: Money,
  opCount: z.number().int().nonnegative(), // "1 та операция" sub-line
})
export type TopParty = z.infer<typeof TopParty>

export const DepartmentSlice = z.object({
  department: Department,
  amount: Money,
  pct: z.number(), // share of total expense, 0-100 float
})
export type DepartmentSlice = z.infer<typeof DepartmentSlice>

export const DashboardAttention = z.object({
  pendingExpenseCount: z.number().int().nonnegative(),
  pendingExpenseAmount: Money,
  pendingInvoiceCount: z.number().int().nonnegative(),
  pendingInvoiceAmount: Money,
})
export type DashboardAttention = z.infer<typeof DashboardAttention>

export const DashboardSummarySchema = z.object({
  period: z.object({ from: z.string(), to: z.string() }),
  kpi: DashboardKpi,
  dailyFlow: z.array(DailyFlowPoint), // ~30 points
  expenseByCategory: z.array(CategorySlice),
  expenseByDepartment: z.array(DepartmentSlice), // 5 visible departments, descending by amount
  accounts: z.array(AccountBalance),
  topSuppliers: z.array(TopParty), // top-5 EXPENSE
  topCustomers: z.array(TopParty), // top-5 INCOME
  recentOps: z.array(TransactionSchema).max(8),
  attention: DashboardAttention,
})
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>

// ─── Spring-pageable envelope ─────────────────────────────────────────────────
// Matches the backend's existing pageable shape used across the app.

export function PageSchema<S extends z.ZodTypeAny>(item: S) {
  return z.object({
    content: z.array(item),
    totalElements: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    number: z.number().int().nonnegative(), // 0-indexed page
    size: z.number().int().nonnegative(),
  })
}

export const TransactionPageSchema = PageSchema(TransactionSchema)
export type TransactionPage = z.infer<typeof TransactionPageSchema>

// ─── Invoice list aggregate (GET /accounting/invoices) ────────────────────────
// The list endpoint piggy-backs a `summary` block on top of the standard
// Spring pageable envelope. `content` reflects the type+status filter applied
// to the request; `summary` is ALWAYS computed over the unfiltered set so the
// 4 header cards (Кутилмоқда / Тўланди / Чиқим / Кирим) and the `Барчаси`
// type-tab count stay stable as the user toggles filters — the same composite
// pattern the pending-payments endpoint uses (`{summary, groups}`). The field
// is REQUIRED because the frontend owns this contract until backend ships:
// callers can rely on `summary` being present, no `?? default` boilerplate.
// Flag for CTO ratification alongside the detail-page txn embed below.

export const InvoiceListSummarySchema = z.object({
  pending: z.object({
    count: z.number().int().nonnegative(),
    sum: z.number().int().nonnegative(),
  }),
  paid: z.object({
    count: z.number().int().nonnegative(),
    sum: z.number().int().nonnegative(),
  }),
  expenseCount: z.number().int().nonnegative(),
  incomeCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
})
export type InvoiceListSummary = z.infer<typeof InvoiceListSummarySchema>

export const InvoicePageSchema = PageSchema(InvoiceSchema).extend({
  summary: InvoiceListSummarySchema,
})
export type InvoicePage = z.infer<typeof InvoicePageSchema>

// ─── Invoice detail (GET /accounting/invoices/{id}) ───────────────────────────
// The detail endpoint embeds the full attached-transaction rows so the page
// can render its left-side attached-txns table + mismatch banner without a
// follow-up `GET /transactions?ids[]` round-trip. Deep-link-safe: a hard
// refresh on `/accounting/invoices/:invoiceId` resolves with a cold list
// cache. Flag for CTO ratification (added alongside the list `summary`).

export const InvoiceDetailSchema = InvoiceSchema.extend({
  transactions: z.array(TransactionSchema),
})
export type InvoiceDetail = z.infer<typeof InvoiceDetailSchema>

// ─── Pending Payments aggregate (GET /accounting/pending-payments — #9) ───────
// Single round-trip snapshot for the "To'lovga kutilmoqda" page: 3 summary
// cards + 2 grouped UNPAID lists. Card #3 (PAYMENT_OPERATION) counts INVOICED
// rows which are NOT in the UNPAID groups, so the page cannot derive it from
// the generic transactions list — hence a dedicated endpoint.
//
// The SELECTED card is intentionally absent: it is composed client-side from
// the active-tab selection (count + Σ amount), never round-tripped.

export const PendingSummaryCardSchema = z.object({
  status: z.enum(['NON_PAYABLE_EXPENSES', 'PENDING_INCOME', 'PAYMENT_OPERATION']),
  totalCount: z.number().int().nonnegative(),
  totalSum: z.number().int().nonnegative(),
  // Only PAYMENT_OPERATION carries a value (`"BANK_IN_PROGRESS"`). The other
  // two cards return `null`. UI label resolves via i18n — no raw Cyrillic on
  // the wire (hard rule).
  paymentType: z.string().nullable(),
})
export type PendingSummaryCard = z.infer<typeof PendingSummaryCardSchema>

export const PendingGroupSchema = z.object({
  type: z.enum(['EXPENSES', 'INCOME']),
  totalCount: z.number().int().nonnegative(),
  items: z.array(TransactionSchema),
})
export type PendingGroup = z.infer<typeof PendingGroupSchema>

export const PendingPaymentsResponseSchema = z.object({
  summary: z.array(PendingSummaryCardSchema).length(3),
  groups: z.array(PendingGroupSchema).length(2),
})
export type PendingPaymentsResponse = z.infer<typeof PendingPaymentsResponseSchema>
